import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquareQuote,
  Plus,
  Edit2,
  Trash2,
  Star,
  User,
  AlertCircle,
} from 'lucide-react';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Badge from '../components/Badge';
import ImageUploader from '../components/ImageUploader';
import { useToast } from '../components/Toast';
import { queries } from '../../lib/supabase/queries';
import { storage } from '../../lib/supabase/storage';
import { isSupabaseConfigured } from '../../lib/supabase/client';

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    client_name: '',
    company: '',
    review: '',
    client_image_url: '',
    rating: 5,
    status: 'published',
    sort_order: 0,
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [formSaving, setFormSaving] = useState(false);

  const toast = useToast();

  const loadTestimonials = useCallback(async () => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      setError('Supabase connection is not configured in .env.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await queries.testimonials.getAll();
      setTestimonials(data);
    } catch (err) {
      console.error('Error loading testimonials:', err);
      setError(err.message || 'Failed to fetch testimonials from Supabase.');
      toast.error('Failed to load testimonials: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadTestimonials();
  }, [loadTestimonials]);

  const handleOpenCreate = () => {
    setIsEditing(false);
    setSelectedTestimonial(null);
    setAvatarFile(null);
    setFormData({
      client_name: '',
      company: '',
      review: '',
      client_image_url: '',
      rating: 5,
      status: 'published',
      sort_order: testimonials.length + 1,
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (t) => {
    setIsEditing(true);
    setSelectedTestimonial(t);
    setAvatarFile(null);
    setFormData({
      client_name: t.client_name,
      company: t.company || '',
      review: t.review,
      client_image_url: t.client_image_url || '',
      rating: t.rating || 5,
      status: t.status || 'published',
      sort_order: t.sort_order || 0,
    });
    setModalOpen(true);
  };

  // Quick 1-click status toggle
  const handleToggleStatus = async (t, e) => {
    e.stopPropagation();
    try {
      await queries.testimonials.toggleStatus(t.id, t.status);
      toast.success(
        `Testimonial status set to ${t.status === 'published' ? 'Draft' : 'Published'}`
      );
      loadTestimonials();
    } catch (err) {
      toast.error('Failed to toggle review status: ' + err.message);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.client_name.trim() || !formData.review.trim()) {
      toast.error('Client name and review text are required');
      return;
    }

    setFormSaving(true);
    try {
      let finalAvatarUrl = formData.client_image_url;

      if (avatarFile) {
        const uploadRes = await storage.uploadFile('testimonial-images', avatarFile);
        finalAvatarUrl = uploadRes.publicUrl;
      }

      const payload = {
        ...formData,
        client_image_url: finalAvatarUrl,
      };

      if (isEditing && selectedTestimonial) {
        await queries.testimonials.update(selectedTestimonial.id, payload);
        toast.success('Testimonial updated in Supabase');
      } else {
        await queries.testimonials.create(payload);
        toast.success('Testimonial published in Supabase');
      }

      setModalOpen(false);
      loadTestimonials();
    } catch (err) {
      console.error('Error saving testimonial:', err);
      toast.error('Failed to save testimonial: ' + err.message);
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTestimonial) return;
    try {
      await queries.testimonials.delete(selectedTestimonial.id);
      toast.success('Testimonial removed from Supabase');
      setConfirmOpen(false);
      setSelectedTestimonial(null);
      loadTestimonials();
    } catch (err) {
      console.error('Error deleting testimonial:', err);
      toast.error('Failed to delete: ' + err.message);
    }
  };

  const columns = [
    {
      header: 'Client',
      accessor: (row) => (
        <div className="flex items-center gap-3">
          {row.client_image_url ? (
            <img
              src={row.client_image_url}
              alt={row.client_name}
              className="w-10 h-10 rounded-full object-cover border border-slate-800 shrink-0 shadow-sm"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 font-bold text-xs shrink-0">
              <User size={16} />
            </div>
          )}
          <div>
            <div className="font-bold text-sm text-slate-100">{row.client_name}</div>
            <div className="text-xs text-slate-400">{row.company || 'Homeowner'}</div>
          </div>
        </div>
      ),
    },
    {
      header: 'Rating',
      accessor: (row) => (
        <div className="flex items-center gap-1 text-amber-400">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              size={14}
              className={i < (row.rating || 5) ? 'fill-amber-400' : 'text-slate-700'}
            />
          ))}
        </div>
      ),
    },
    {
      header: 'Review Feedback',
      accessor: (row) => (
        <p className="text-xs text-slate-300 line-clamp-2 max-w-sm italic">
          "{row.review}"
        </p>
      ),
    },
    {
      header: 'Status (Click to Toggle)',
      accessor: (row) => (
        <button
          onClick={(e) => handleToggleStatus(row, e)}
          className="group focus:outline-none transition active:scale-95"
          title="Click to toggle Published / Draft"
        >
          <Badge variant={row.status === 'published' ? 'success' : 'warning'}>
            {row.status === 'published' ? 'PUBLISHED' : 'DRAFT'}
          </Badge>
        </button>
      ),
    },
    {
      header: 'Actions',
      className: 'text-right',
      accessor: (row) => (
        <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => handleOpenEdit(row)}
            className="p-2 rounded-xl text-slate-400 hover:text-[#9BB7A3] hover:bg-[#9BB7A3]/10 transition"
            title="Edit Review"
          >
            <Edit2 size={15} />
          </button>
          <button
            onClick={() => {
              setSelectedTestimonial(row);
              setConfirmOpen(true);
            }}
            className="p-2 rounded-xl text-slate-400 hover:text-rose-400 hover:bg-rose-950/30 transition"
            title="Delete Review"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900/60 p-6 rounded-3xl border border-slate-800">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2.5">
            <MessageSquareQuote size={24} className="text-[#9BB7A3]" />
            Client Reviews & Testimonials
          </h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Manage genuine client reviews, star ratings, and publication status displayed on the public website.
          </p>
        </div>

        <button
          onClick={handleOpenCreate}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-[#9BB7A3]/20 active:scale-95 shrink-0"
        >
          <Plus size={16} />
          <span>Add Review</span>
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-2xl bg-rose-950/40 border border-rose-800/40 text-rose-300 text-xs flex items-center gap-2.5">
          <AlertCircle size={16} className="shrink-0 text-rose-400" />
          <span>{error}</span>
        </div>
      )}

      {/* Main DataTable */}
      <DataTable
        loading={loading}
        columns={columns}
        data={testimonials}
        searchKey="client_name"
        searchPlaceholder="Search reviews by client name..."
        filters={[
          {
            key: 'status',
            label: 'Status',
            options: [
              { label: 'Published', value: 'published' },
              { label: 'Draft', value: 'draft' },
            ],
          },
        ]}
        emptyIcon={<MessageSquareQuote size={40} className="text-slate-600" />}
        emptyTitle="No Testimonials Found"
        emptyDescription="Add client reviews to establish trust and credibility on your studio website."
        onAddNew={handleOpenCreate}
        addNewLabel="Add Review"
      />

      {/* Create / Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => !formSaving && setModalOpen(false)}
        title={isEditing ? 'Edit Client Review' : 'Add Client Review'}
        maxWidth="max-w-2xl"
      >
        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Client Name <span className="text-rose-400">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                placeholder="e.g., Rajesh Kumar"
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Company / Project Title
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="e.g., 3BHK Villa Homeowner, Tiruppur"
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Interactive Star Rating Selector */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Star Rating ({formData.rating} / 5)
              </label>
              <div className="flex items-center gap-1.5 p-2 bg-slate-950 rounded-xl border border-slate-800">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFormData({ ...formData, rating: star })}
                    className="p-1 focus:outline-none transition-transform hover:scale-125"
                  >
                    <Star
                      size={20}
                      className={
                        star <= formData.rating
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-slate-700 hover:text-amber-300'
                      }
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Publication Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              >
                <option value="published">Published (Public)</option>
                <option value="draft">Draft (Hidden)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Sort Order
              </label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) =>
                  setFormData({ ...formData, sort_order: parseInt(e.target.value, 10) || 0 })
                }
                className="w-full px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
              Review Testimony <span className="text-rose-400">*</span>
            </label>
            <textarea
              rows={4}
              required
              value={formData.review}
              onChange={(e) => setFormData({ ...formData, review: e.target.value })}
              placeholder="Client's review of the design, woodwork, modular kitchen, execution timeline, and overall experience..."
              className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
            />
          </div>

          {/* Avatar Image Uploader */}
          <div className="bg-slate-950/60 p-4 rounded-2xl border border-slate-800/80">
            <ImageUploader
              label="Client Avatar Photo (Optional)"
              currentImageUrl={formData.client_image_url}
              value={formData.client_image_url}
              onImageSelected={(file) => setAvatarFile(file)}
              onImageRemoved={() => {
                setAvatarFile(null);
                setFormData((prev) => ({ ...prev, client_image_url: '' }));
              }}
              onChange={(url) => setFormData((prev) => ({ ...prev, client_image_url: url }))}
              recommendedSize="Recommended: 400x400 JPG/PNG portrait"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-800">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={formSaving}
              className="px-6 py-2.5 rounded-xl bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-[#9BB7A3]/20 disabled:opacity-50"
            >
              {formSaving ? 'Saving to Database...' : isEditing ? 'Update Review' : 'Publish Review'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setSelectedTestimonial(null);
        }}
        onConfirm={handleDelete}
        title="Delete Testimonial"
        message={`Are you sure you want to delete the review by "${selectedTestimonial?.client_name}"?`}
        confirmText="Delete Review"
        type="danger"
      />
    </div>
  );
}
